import { toast } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { useDeletePerformer, usePerformersQuery } from "../api/queries";
import { PerformerFilter } from "../ui/PerformerFilter";
import { PerformerTable } from "../ui/PerformerTable";
import { PerformerToolbar } from "../ui/PerformerToolbar";

export function PerformersPage() {
  const nav = useNavigate({ from: "/app/performance/performers" });
  const search: OffsetPageQueryDTO = useSearch({
    from: "/app/performance/performers",
  });

  const list = usePerformersQuery({
    offset: search.offset,
    limit: search.limit,
    sort: search.sort,
    filters: search.filters,
  });

  const canCreate = useActionPermission(["performers:create"]);
  const deletePerformer = useDeletePerformer();

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ນັກສະແດງ" },
          ]}
        />
      </Header>

      <Main>
        <PerformerToolbar
          canManage={!!canCreate}
          onCreate={() => nav({ to: "/app/performance/performers/create" })}
        />

        <div className="flex flex-col rounded-xl border bg-card pt-2">
          <PerformerFilter />

          <PerformerTable
            data={list.data?.data ?? []}
            isLoading={list.isLoading}
            offset={search.offset ?? 0}
            limit={search.limit ?? 20}
            totalCount={list.data?.meta?.total ?? 0}
            onPaginationChange={(offset, limit) =>
              nav({ search: { ...search, offset, limit } })
            }
            sortBy={search.sort ? search.sort[0]?.field : undefined}
            sortOrder={
              search.sort ? (search.sort[0]?.dir as "asc" | "desc") : undefined
            }
            onSortingChange={(id, desc) =>
              nav({
                search: {
                  ...search,
                  sort: [{ field: id, dir: desc ? "desc" : "asc" }],
                },
              })
            }
            onView={(performer) =>
              nav({
                to: "/app/performance/performers/$id",
                params: { id: performer.id },
              })
            }
            onEdit={(performer) =>
              nav({
                to: "/app/performance/performers/$id/edit",
                params: { id: performer.id },
              })
            }
            onDelete={(id) => {
              toast.promise(deletePerformer.run(id), {
                loading: "ກຳລັງລຶບ...",
                success: "ລຶບນັກສະແດງສຳເລັດ",
                error: "ລຶບນັກສະແດງລົ້ມເຫຼວ",
              });
            }}
          />
        </div>
      </Main>
    </>
  );
}
